// @file Surface ai chat store

import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { SourcedFile } from '@@/bits/surface_ai_chat_helper'
import {
  DEFAULT_LOADING_MESSAGE_KEY,
  resolveSurfaceAiChatLoadingMessage,
} from '@@/bits/surface_ai_chat_loading_messages'
import { unboundedWatch } from '@@/bits/vue'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAIChatToolsStore } from '@@/pinia/surface_ai_chat_tools_store'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useAIChatSurfaceStrategy } from '@@/vuecomposables/useAIChatSurfaceStrategy'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum Role {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

interface MessageAttachment {
  name: string
  content_type: string
  url: string
}

interface MessageProps {
  approval_status?: boolean | null
  has_revert_plan?: boolean
}

interface Message {
  id?: string
  role: string
  content: string
  created_at: number
  completed_at: number | null
  attachments: MessageAttachment[]
  props: MessageProps
}

enum UploadedFileStatus {
  Uploading = 'Uploading',
  Completed = 'Completed',
}

interface UploadedFile {
  id: string
  file: SourcedFile
  status: UploadedFileStatus
}

interface MessageExtraData {
  files: UploadedFile['file'][]
}

interface Suggestion {
  long_message: string
  short_message: string
  created_at: number
}

interface RawMessage {
  object: string
  id: string
  assistant_id: string | null
  attachments: MessageAttachment[]
  completed_at: number | null
  content: Array<{
    type: string
    text: {
      annotations: unknown[]
      value: string
    }
  }>
  created_at: number
  incomplete_at: number | null
  incomplete_details: unknown | null
  metadata: Record<string, unknown>
  role: string
  run_id: string | null
  status: string | null
  thread_id: string
  props: MessageProps
}

enum SuggestionsStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

enum PanelStatus {
  LOADING = 'loading',
  INTRODUCTION = 'introduction',
  CHAT = 'chat',
  ERROR = 'error',
}

const INITIAL_SUGGESTIONS_COUNT = 3
const ASSISTANT_MESSAGE_DELAY_MS = 300
const RESPONSE_TIMEOUT_MS = 120_000 // 2 minutes

// Timestamps returned by the backend are in seconds instead of milliseconds.
const formatTimestamp = (timestamp: number): number => timestamp * 1000

const useSurfaceAIChatStore = defineStore('surfaceAIChatStore', () => {
  const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceAIChatToolsStore = useSurfaceAIChatToolsStore()
  const { aiChatSurfaceStrategy } = useAIChatSurfaceStrategy()
  // aiChatSurfaceStrategy is a computed ref that reactively selects the correct
  // strategy (board vs whiteboard). Wrap derived properties in computed so they
  // track the strategy switch.
  const xAiChat = computed(() => aiChatSurfaceStrategy.value.xAiChat.value)
  const introduction = computed(() => aiChatSurfaceStrategy.value.introduction)
  const personalizedTools = computed(() => aiChatSurfaceStrategy.value.personalizedTools.value)

  const xSurfaceAIChatPanel = ref(false)
  const panelStatus = ref<PanelStatus>(PanelStatus.LOADING)
  const xActionMenu = ref(false)
  const magicWallFirstOpen = ref(false)
  const currentChatStartTimestamp = ref<number>(Infinity)
  const messages = ref<Message[]>([])
  const threadId = ref<string | null>(null)
  const savedThreadId = ref<string | null>(null)
  const lastAssistantChunkIndex = ref<number | null>(null)
  const assistantPlaceholderPushed = ref(false)
  const uploadedFiles = ref<UploadedFile[]>([])

  const suggestions = ref<Suggestion[]>([])
  const suggestionsStatus = ref<SuggestionsStatus>(SuggestionsStatus.SUCCESS)
  const greetingMessage = ref<string | null>(null)

  const isLoadingSuggestions = computed(() => suggestionsStatus.value === SuggestionsStatus.LOADING)
  const hasSuggestions = computed(() => suggestions.value.length !== 0)
  const hasGreeting = computed(() => greetingMessage.value !== null)

  const assistantName = computed(() => {
    return isAppUsing('piChatV2') ? __('Padlet AI') : __('Padlet Intelligence')
  })

  const hasMessages = computed(() => messages.value.length > 0)
  const isLoadingResponse = ref(false)

  /** Backend `loading_message_key`; null means hide the loading line (e.g. while streaming text). */
  const loadingStatusMessageKey = ref<string | null>(null)

  const loadingStatusMessage = computed((): string => {
    const key = loadingStatusMessageKey.value
    if (key === null) return ''
    return resolveSurfaceAiChatLoadingMessage(key)
  })

  const hasFinishedResponse = computed(() => {
    return !isLoadingResponse.value && !surfaceAIChatToolsStore.isRunningTool
  })

  // magicWallFirstOpen is used by SurfaceContainer to control visibility of SurfaceMagicWallFeedbackPopup
  async function loadMagicWallFirstOpen(_showSurfaceChat: boolean): Promise<void> {
    const jsonFormData = safeLocalStorage.getItem('magicWallOptions')
    if (jsonFormData != null) {
      magicWallFirstOpen.value = true
      safeLocalStorage.removeItem('magicWallOptions')
    }
  }

  async function loadThreadMessages(threadId: string, signal?: AbortSignal): Promise<void> {
    try {
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      const response = await SurfaceAIChatApi.getThreadMessages(threadId, surfaceStore.wallHashid, { signal })
      const formattedMessages = response.messages
        .filter((message: RawMessage) => {
          // Only show assistant and user messages, filter out system messages
          if (message.role === 'assistant' || message.role === 'user') {
            const content = message.content[0].text.value
            // Filter out old context messages that shouldn't be displayed
            if (
              content.includes('# user context #') ||
              content.includes('# padlet contents #') ||
              content.includes('# initial instructions #')
            ) {
              return false
            }
            return true
          }
          return false
        })
        .map((message: RawMessage): Message => {
          return {
            id: message.id,
            role: message.role,
            content: message.content[0].text.value,
            created_at: formatTimestamp(message.created_at),
            completed_at: message.completed_at != null ? formatTimestamp(message.completed_at) : null,
            attachments: message.attachments,
            props: message.props ?? {},
          }
        })

      messages.value = formattedMessages
    } catch (error) {
      messages.value = []
      savedThreadId.value = null
      throw error
    }
  }

  async function initializeAll(): Promise<void> {
    panelStatus.value = PanelStatus.LOADING
    try {
      if (messages.value.length > 0) {
        panelStatus.value = PanelStatus.CHAT
        return
      }
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      // Try to get existing thread
      savedThreadId.value = await SurfaceAIChatApi.getThreadId(surfaceStore.wallId, surfaceStore.wallHashid)

      if (savedThreadId.value != null) {
        // Load existing thread messages
        threadId.value = savedThreadId.value
        await loadThreadMessages(savedThreadId.value, AbortSignal.timeout(7000))
      } else {
        const response = await SurfaceAIChatApi.createThread(surfaceStore.wallHashid)
        threadId.value = response.thread_id
      }

      if (magicWallFirstOpen.value) {
        panelStatus.value = PanelStatus.CHAT
        magicWallFirstOpen.value = false
      } else if (messages.value.length > 0) {
        panelStatus.value = PanelStatus.CHAT
      } else {
        panelStatus.value = PanelStatus.INTRODUCTION
      }
      await fetchSuggestions(messages.value.length === 0)
    } catch (error) {
      if (error.name === 'TimeoutError') {
        // If loading messages takes too long, set threadId to null
        // Don't throw error or it will be handled as a general initialization error
        savedThreadId.value = null
        threadId.value = null
      }
      panelStatus.value = PanelStatus.ERROR
    }
  }

  async function fetchSuggestions(includeGreeting: boolean = false): Promise<void> {
    if (!aiChatSurfaceStrategy.value.shouldFetchSuggestionsFromApi.value) {
      suggestions.value = aiChatSurfaceStrategy.value.fallbackSuggestions
      greetingMessage.value = null
      suggestionsStatus.value = SuggestionsStatus.SUCCESS
      return
    }
    try {
      suggestionsStatus.value = SuggestionsStatus.LOADING
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      const response = await SurfaceAIChatApi.getSuggestions(surfaceStore.wallHashid, {
        signal: AbortSignal.timeout(5000), // This signal will cause getSuggestions to throw a timeout error if the request takes more than 5 seconds
        includeGreeting,
      })

      if ('message' in response && response.message) {
        greetingMessage.value = response.message
      } else {
        greetingMessage.value = null
      }
      const fetchedSuggestions = response.suggestions.map((suggestion: Suggestion) => ({
        ...suggestion,
        created_at: suggestion.created_at != null ? formatTimestamp(suggestion.created_at) : Date.now(),
      }))

      // Always guarantee a minimum of 3 suggestions
      suggestions.value = [...fetchedSuggestions, ...aiChatSurfaceStrategy.value.fallbackSuggestions].slice(
        0,
        INITIAL_SUGGESTIONS_COUNT,
      )

      suggestionsStatus.value = SuggestionsStatus.SUCCESS
    } catch {
      suggestions.value = aiChatSurfaceStrategy.value.fallbackSuggestions
      greetingMessage.value = null
      suggestionsStatus.value = SuggestionsStatus.ERROR
    }
  }

  async function onUserMessage(message: Message): Promise<void> {
    // We start the response timeout when the user submits a message.
    const files = [...(uploadedFiles.value ?? [])]
    clearUploadedFiles()
    startResponseTimeout()
    const userMessage = pushUserMessage(message.content, files)

    isLoadingResponse.value = true
    loadingStatusMessageKey.value = DEFAULT_LOADING_MESSAGE_KEY
    assistantPlaceholderPushed.value = false
    setTimeout(() => {
      if (!assistantPlaceholderPushed.value) {
        pushAssistantMessage('')
      }
    }, ASSISTANT_MESSAGE_DELAY_MS)
    const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')

    if (threadId.value === null) {
      const response = await SurfaceAIChatApi.createThread(surfaceStore.wallHashid)
      threadId.value = response.thread_id
    }
    // Save thread on user message
    if (savedThreadId.value == null && threadId.value != null) {
      void SurfaceAIChatApi.saveThreadId(surfaceStore.wallId, threadId.value)
      savedThreadId.value = threadId.value
    }
    const response = await SurfaceAIChatApi.sendMessage(threadId.value as string, surfaceStore.wallHashid, message, {
      files: files.map((uploadedFile: UploadedFile): SourcedFile => uploadedFile.file),
    })

    if (response?.message_id != null) {
      userMessage.id = response.message_id
    }
  }

  function addAssistantMessageDelta(
    delta: string,
    finalMessage: boolean = false,
    chunkIndex?: number,
    messageId?: string,
    hasRevertPlan?: boolean,
  ): void {
    ensureAssistantPlaceholder()
    const lastMessage = messages.value[messages.value.length - 1]
    // chunks are streamed with index in order
    // use it to detect and discard duplicate chunks (due to network, job retries, etc.)
    // this works because user always have to wait for the LLM's full response before sending a new message
    if (chunkIndex != null) {
      if (lastAssistantChunkIndex.value != null && chunkIndex <= lastAssistantChunkIndex.value) {
        return
      }
      lastAssistantChunkIndex.value = chunkIndex
    }

    if (delta.length > 0) {
      loadingStatusMessageKey.value = null
      lastMessage.content += delta
    }
    if (finalMessage) {
      lastMessage.completed_at = Date.now()
      lastAssistantChunkIndex.value = null
      if (messageId != null) {
        lastMessage.id = messageId
      }
      if (hasRevertPlan === true) {
        lastMessage.props.has_revert_plan = true
      }
    }
  }

  function pushAssistantMessage(content: string): void {
    messages.value.push({
      role: Role.ASSISTANT,
      content,
      created_at: Date.now(),
      completed_at: null,
      attachments: [],
      props: {},
    })
    assistantPlaceholderPushed.value = true
    lastAssistantChunkIndex.value = null
  }

  function ensureAssistantPlaceholder(): void {
    // If the assistant placeholder hasn't been pushed yet (ASSISTANT_MESSAGE_DELAY_MS timer hasn't fired),
    // push it now so deltas don't mutate the user message.
    if (!assistantPlaceholderPushed.value) {
      pushAssistantMessage('')
    }
  }

  function pushUserMessage(content: string, files: UploadedFile[]): Message {
    const userMessage: Message = {
      role: Role.USER,
      content,
      created_at: Date.now(),
      completed_at: Date.now(),
      attachments: files.map(
        (file: UploadedFile): MessageAttachment => ({
          name: file.file.name,
          content_type: file.file.type,
          url: file.file.url || URL.createObjectURL(file.file),
        }),
      ),
      props: {},
    }
    messages.value = [...messages.value, userMessage]
    return messages.value[messages.value.length - 1]
  }

  function showChatPanel(): void {
    xSurfaceAIChatPanel.value = true
    currentChatStartTimestamp.value = Date.now()
  }

  function openActionMenu(): void {
    xActionMenu.value = true
  }

  function hidePostActionMenu(): void {
    xActionMenu.value = false
  }

  function setPanelStatus(status: PanelStatus): void {
    panelStatus.value = status
  }

  async function clearChatMessages(): Promise<void> {
    const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
    const response = await SurfaceAIChatApi.clearChatMessages(surfaceStore.wallHashid)
    if (response?.status === 'ok') {
      messages.value = []
      clearUploadedFiles()
      clearErrorMessage()
      greetingMessage.value = null
      panelStatus.value = PanelStatus.INTRODUCTION
      void fetchSuggestions(true)
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Chat messages cleared successfully'),
      })
    } else {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Error clearing chat messages'),
      })
    }
  }

  function toggleChatPanel(): void {
    if (surfaceOnboardingPanelStore.showOnboardingPanel) {
      surfaceOnboardingPanelStore.closeOnboardingPanel()
    }
    if (!xSurfaceAIChatPanel.value) {
      initializeAll()
      showChatPanel()
    } else {
      xSurfaceAIChatPanel.value = false
      clearErrorMessage()
    }
  }

  function setUploadedFiles(files: UploadedFile[]): void {
    uploadedFiles.value = files
  }

  function clearUploadedFiles(): void {
    uploadedFiles.value = []
  }

  const errorMessage = ref<string>('')
  const hasErrorMessage = computed(() => errorMessage.value !== '')
  const errorMessageTimeoutId = ref<number | null>(null)

  function setErrorMessage(message: string): void {
    if (errorMessageTimeoutId.value != null) {
      window.clearTimeout(errorMessageTimeoutId.value)
      errorMessageTimeoutId.value = null
    }
    errorMessage.value = message
    errorMessageTimeoutId.value = window.setTimeout(() => {
      clearErrorMessage()
    }, 3000)
  }

  function clearErrorMessage(): void {
    errorMessage.value = ''
    if (errorMessageTimeoutId.value != null) {
      window.clearTimeout(errorMessageTimeoutId.value)
      errorMessageTimeoutId.value = null
    }
  }

  const responseTimeoutId = ref<number | null>(null)

  function startResponseTimeout() {
    responseTimeoutId.value = window.setTimeout(() => {
      handleResponseTimeout()
    }, RESPONSE_TIMEOUT_MS)
  }

  function handleResponseTimeout(): void {
    isLoadingResponse.value = false
    loadingStatusMessageKey.value = null

    globalSnackbarStore.setSnackbar({
      message: __('The assistant is taking longer than expected. Please try again.'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function clearResponseTimeout() {
    if (responseTimeoutId.value === null) return
    clearTimeout(responseTimeoutId.value)
    responseTimeoutId.value = null
  }

  async function updateMessageApprovalStatus(message: Message, approved: boolean): Promise<void> {
    if (message.id == null) return
    try {
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      if (approved) {
        await SurfaceAIChatApi.approveMessage(surfaceStore.wallHashid, message.id)
      } else {
        await SurfaceAIChatApi.disapproveMessage(surfaceStore.wallHashid, message.id)
      }
      message.props.approval_status = approved
      globalSnackbarStore.setSnackbar({
        message: __('Thank you for your feedback'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Oops! Something went wrong. Please try again.'),
        notificationType: SnackbarNotificationType.error,
      })
    }
  }

  async function approveMessage(message: Message): Promise<void> {
    await updateMessageApprovalStatus(message, true)
  }

  async function disapproveMessage(message: Message): Promise<void> {
    await updateMessageApprovalStatus(message, false)
  }

  const isRevertingMessage = ref(false)

  async function revertMessage(message: Message): Promise<void> {
    if (message.id == null) return
    isRevertingMessage.value = true
    try {
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      const result = await SurfaceAIChatApi.revertMessage(surfaceStore.wallHashid, message.id)
      if (result.status === 'success' || result.status === 'partial') {
        message.props.has_revert_plan = false
        globalSnackbarStore.setSnackbar({
          message: result.status === 'success' ? 'Changes undone successfully' : 'Some changes could not be undone',
          notificationType:
            result.status === 'success' ? SnackbarNotificationType.success : SnackbarNotificationType.warning,
        })
      } else if (result.status === 'already_applied') {
        message.props.has_revert_plan = false
        globalSnackbarStore.setSnackbar({
          message: 'Changes have already been undone',
          notificationType: SnackbarNotificationType.success,
        })
      } else {
        globalSnackbarStore.setSnackbar({
          message: 'Unable to undo changes',
          notificationType: SnackbarNotificationType.error,
        })
      }
    } catch {
      globalSnackbarStore.setSnackbar({
        message: 'Unable to undo changes',
        notificationType: SnackbarNotificationType.error,
      })
    } finally {
      isRevertingMessage.value = false
    }
  }

  void unboundedWatch(
    () => hasFinishedResponse.value,
    (finished: boolean) => {
      if (finished) {
        clearResponseTimeout()
      }
    },
  )

  return {
    loadThreadMessages,
    xAiChat,
    xActionMenu,
    suggestions,
    hasSuggestions,
    greetingMessage,
    hasGreeting,
    assistantName,
    messages,
    uploadedFiles,
    errorMessage,
    hasErrorMessage,
    threadId,
    savedThreadId,
    hasMessages,
    onUserMessage,
    pushUserMessage,
    showChatPanel,
    openActionMenu,
    hidePostActionMenu,
    toggleChatPanel,
    setPanelStatus,
    clearChatMessages,
    fetchSuggestions,
    xSurfaceAIChatPanel,
    panelStatus,
    currentChatStartTimestamp,
    magicWallFirstOpen,
    isLoadingSuggestions,
    isLoadingResponse,
    loadingStatusMessageKey,
    loadingStatusMessage,
    hasFinishedResponse,
    loadMagicWallFirstOpen,
    initializeAll,
    addAssistantMessageDelta,
    ensureAssistantPlaceholder,
    handleResponseTimeout,
    clearResponseTimeout,
    setUploadedFiles,
    clearUploadedFiles,
    introduction,
    personalizedTools,
    setErrorMessage,
    clearErrorMessage,
    approveMessage,
    disapproveMessage,
    revertMessage,
    isRevertingMessage,
  }
})
export type { AIChatPersonalizedTool } from '@@/vuecomposables/useAIChatSurfaceStrategy'
export {
  INITIAL_SUGGESTIONS_COUNT,
  PanelStatus,
  Role,
  SourcedFile,
  SuggestionsStatus,
  UploadedFileStatus,
  useSurfaceAIChatStore,
}
export type { Message, MessageExtraData, RawMessage, Suggestion, UploadedFile }
